import { createSlice } from "@reduxjs/toolkit";
import {
  getPenaltiesThunk,
  getPenaltyByIdThunk,
  getPenaltiesByPKSThunk,
  getPenaltiesByMitraThunk,
  getOverduePenaltiesThunk,
  createPenaltyThunk,
  createPenaltyFromInvoiceThunk,
  updatePenaltyThunk,
  deletePenaltyThunk,
  issuePenaltyThunk,
  recordPenaltyPaymentThunk,
  waivePenaltyThunk,
  disputePenaltyThunk,
  resolveDisputePenaltyThunk,
} from "state/action/penalty";

// ── Helper ────────────────────────────────────────────────────────────────────
const asyncState = (data = null) => ({
  data,
  isFulfilled: false,
  isPending: false,
  isRejected: false,
  err: null,
});
const pending   = (s) => ({ ...s, isPending: true,  isFulfilled: false, isRejected: false, err: null });
const fulfilled = (s, data) => ({ ...s, isPending: false, isFulfilled: true, data });
const rejected  = (s, err)  => ({ ...s, isPending: false, isRejected: true, err });

// ── Initial form ─────────────────────────────────────────────────────────────
export const initialPenaltyForm = {
  pks_id: "",
  mitra: "",
  penalty_type: "",          // "late_payment" | "late_performance" | "contract_breach" | "other"
  violation_description: "",
  violation_date: "",
  related_invoice: "",
  related_warning_letter: "",
  calculation: {
    base_amount: "",
    days_late: "",
  },
  due_date: "",
  notes: "",
};

// ── Initial state ─────────────────────────────────────────────────────────────
const initialState = {
  getPenalties:            asyncState(),
  getPenaltiesByPKS:       asyncState(),
  getPenaltiesByMitra:     asyncState(),
  getOverduePenalties:     asyncState(),
  getPenaltyById:          asyncState(),
  createPenalty:           asyncState(),
  createPenaltyFromInvoice: asyncState(),
  updatePenalty:           asyncState(),
  deletePenalty:           asyncState(),
  issuePenalty:            asyncState(),
  recordPenaltyPayment:    asyncState(),
  waivePenalty:            asyncState(),
  disputePenalty:          asyncState(),
  resolveDisputePenalty:   asyncState(),

  penaltyForm: { ...initialPenaltyForm },
  selectedPenalty: null,
};

// ── Slice ─────────────────────────────────────────────────────────────────────
export const penaltySlice = createSlice({
  name: "penalty",
  initialState,
  reducers: {
    setPenaltyForm: (state, action) => {
      state.penaltyForm = action.payload;
    },
    updatePenaltyFormField: (state, action) => {
      const { field, value } = action.payload;
      state.penaltyForm[field] = value;
    },
    updatePenaltyCalculationField: (state, action) => {
      const { field, value } = action.payload;
      state.penaltyForm.calculation[field] = value;
    },
    resetPenaltyForm: (state) => {
      state.penaltyForm = { ...initialPenaltyForm };
    },
    setSelectedPenalty: (state, action) => {
      state.selectedPenalty = action.payload;
    },
    clearSelectedPenalty: (state) => {
      state.selectedPenalty = null;
    },
    clearPenaltiesByPKS: (state) => { state.getPenaltiesByPKS = asyncState(); },
    clearCreatePenalty:  (state) => { state.createPenalty = asyncState(); },
    clearUpdatePenalty:  (state) => { state.updatePenalty = asyncState(); },
    clearDeletePenalty:  (state) => { state.deletePenalty = asyncState(); },
    clearWorkflowStates: (state) => {
      state.issuePenalty         = asyncState();
      state.recordPenaltyPayment = asyncState();
      state.waivePenalty         = asyncState();
      state.disputePenalty       = asyncState();
      state.resolveDisputePenalty = asyncState();
    },
  },
  extraReducers(builder) {
    const map = [
      [getPenaltiesThunk,            "getPenalties"],
      [getPenaltyByIdThunk,          "getPenaltyById"],
      [getPenaltiesByPKSThunk,       "getPenaltiesByPKS"],
      [getPenaltiesByMitraThunk,     "getPenaltiesByMitra"],
      [getOverduePenaltiesThunk,     "getOverduePenalties"],
      [createPenaltyThunk,           "createPenalty"],
      [createPenaltyFromInvoiceThunk,"createPenaltyFromInvoice"],
      [updatePenaltyThunk,           "updatePenalty"],
      [deletePenaltyThunk,           "deletePenalty"],
      [issuePenaltyThunk,            "issuePenalty"],
      [recordPenaltyPaymentThunk,    "recordPenaltyPayment"],
      [waivePenaltyThunk,            "waivePenalty"],
      [disputePenaltyThunk,          "disputePenalty"],
      [resolveDisputePenaltyThunk,   "resolveDisputePenalty"],
    ];

    map.forEach(([thunk, key]) => {
      builder
        .addCase(thunk.pending,   (state) => { state[key] = pending(state[key]); })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key] = fulfilled(state[key], action.payload);
          if (key === "getPenaltyById") {
            state.selectedPenalty = action.payload?.data ?? action.payload;
          }
        })
        .addCase(thunk.rejected,  (state, action) => {
          state[key] = rejected(state[key], action.error.message);
        });
    });
  },
});

export const penaltyAction = {
  ...penaltySlice.actions,
  getPenaltiesThunk,
  getPenaltyByIdThunk,
  getPenaltiesByPKSThunk,
  getPenaltiesByMitraThunk,
  getOverduePenaltiesThunk,
  createPenaltyThunk,
  createPenaltyFromInvoiceThunk,
  updatePenaltyThunk,
  deletePenaltyThunk,
  issuePenaltyThunk,
  recordPenaltyPaymentThunk,
  waivePenaltyThunk,
  disputePenaltyThunk,
  resolveDisputePenaltyThunk,
};

// ── Selectors ─────────────────────────────────────────────────────────────────
export const selectPenalty        = (state) => state.penalty;
export const selectPenaltyForm    = (state) => state.penalty?.penaltyForm;
export const selectSelectedPenalty = (state) => state.penalty?.selectedPenalty;
export const selectPenaltiesByPKS = (state) =>
  state.penalty?.getPenaltiesByPKS?.data?.data ?? [];
export const selectPenaltySummary = (state) =>
  state.penalty?.getPenaltiesByPKS?.data?.summary ?? null;

export default penaltySlice.reducer;
